// Preset + sweep-template loading. Presets live in a JSON document owned by the
// presets agent (web/src/presets/presets.json). This module types it and offers
// helpers to turn a preset into Settings and a template into a SweepSpec.

import presetsDoc from '@/presets/presets.json';
import type { Settings, SweepSpec } from '@/engine/types';

/** A named traffic profile that fills the simple/advanced settings form. */
export interface Preset {
  id: string;
  name: string;
  sizes: number[];
  pps: number;
  durationMs: number;
  acceptableDelayMs: number;
}

/** A named sweep template that seeds the matrix editor. */
export interface SweepTemplate {
  id: string;
  name: string;
  sizes?: number[];
  pps?: number[];
  pairs?: [number, number][];
  durationMs: number;
  acceptableDelayMs: number;
  mode: string;
  repeats: number;
}

interface PresetsDocument {
  version: number;
  note?: string;
  presets: Preset[];
  sweepTemplates: SweepTemplate[];
}

const doc = presetsDoc as unknown as PresetsDocument;

/** All measurement presets. */
export const PRESETS: Preset[] = doc.presets ?? [];

/** All sweep templates. */
export const SWEEP_TEMPLATES: SweepTemplate[] = doc.sweepTemplates ?? [];

/** Look up a preset by id. */
export function presetById(id: string): Preset | undefined {
  return PRESETS.find((p) => p.id === id);
}

/** Look up a sweep template by id. */
export function sweepTemplateById(id: string): SweepTemplate | undefined {
  return SWEEP_TEMPLATES.find((t) => t.id === id);
}

/**
 * Apply a preset to the current settings, keeping the fields presets do not
 * carry (warmupMs) intact.
 */
export function presetToSettings(preset: Preset, warmupMs: number): Settings {
  return {
    sizes: [...preset.sizes],
    pps: preset.pps,
    durationMs: preset.durationMs,
    acceptableDelayMs: preset.acceptableDelayMs,
    warmupMs,
    ipMode: 'auto',
  };
}

/**
 * Turn a sweep template + a node selection into a full SweepSpec. Sweep cells
 * always run sequentially (constraint 1); `order` defaults to size-major.
 */
export function templateToSpec(
  template: SweepTemplate,
  nodeIds: string[],
  warmupMs: number,
): SweepSpec {
  return {
    nodes: [...nodeIds],
    sizes: template.sizes ? [...template.sizes] : [],
    pps: template.pps ? [...template.pps] : [],
    pairs: template.pairs ? template.pairs.map((p) => [p[0], p[1]] as [number, number]) : undefined,
    durationMs: template.durationMs,
    acceptableDelayMs: template.acceptableDelayMs,
    warmupMs,
    repeats: template.repeats,
    order: 'size-major',
  };
}
